import { useCubeQuery } from '@cubejs-client/react';
import {
  BarometerChart,
  formatNumber,
  NumberFormatPreset,
  QuickViewWidget,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { Status } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { colorFromStatus } from '~/util/conversion';

const formatValuePercent = formatNumber(NumberFormatPreset.PERCENT_DECIMAL);

export const ContractUtilizationQuickViewWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractUtilizationQuickViewWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'contract-utilization' });

  const cube = 'ContractUtilization';

  const queryValue = useCubeQuery({
    dimensions: [`${cube}.utilization`],
    filters: [
      {
        member: `${cube}.contractId`,
        operator: 'equals',
        values: [entityId],
      },
    ],
  });

  const nodes = queryValue.resultSet?.rawData()?.map((item) => ({
    utilization: Number(item[`${cube}.utilization`]),
  }));
  const contractUtilization = nodes?.[0]?.utilization ?? 0;

  const status = !contractUtilization
    ? Status.NEUTRAL
    : contractUtilization <= 0.8
      ? Status.SUCCESS
      : contractUtilization <= 0.9
        ? Status.WARNING
        : Status.ERROR;
  const color = colorFromStatus(status);

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={queryValue.isLoading}
      title={t('title')}
      subTitle={t('subTitle')}
      value={formatValuePercent(contractUtilization)}
      data-intercom-target="ContractUtilizationQuickViewWidget"
    >
      {contractUtilization !== undefined ? (
        <BarometerChart
          color={color}
          width={128}
          height={70}
          value={contractUtilization * 100}
          min={0}
          max={100}
          dotColor={color}
        />
      ) : (
        <PlaceholderWidget />
      )}
    </QuickViewWidget>
  );
};
